import { saveAs } from 'file-saver';
import { DownloadIcon, PauseIcon, PlayIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useWaveformPlayer } from '@/hooks/useWaveformPlayer';
import { CallRecording } from '@/shared/types/analytics';
import { formatSecondsToMmSs } from '@/util/dateUtils';

const PLAYBACK_RATES = [0.5, 1, 1.5, 2] as const;

interface CallRecordingPlayerProps {
  recording: CallRecording;
  recordingUrl: string;
}

/**
 * Audio player component for voice call recordings with waveform visualization.
 * Shows play/pause controls, time display, and download option.
 *
 * Features click-to-seek and drag-to-scrub on the waveform for easy navigation.
 */
export const CallRecordingPlayer = ({ recording, recordingUrl }: CallRecordingPlayerProps) => {
  const { t } = useTranslation();
  const isCompleted = recording.recording_status === 'completed';

  const {
    containerRef,
    isReady,
    isPlaying,
    currentTime,
    duration,
    isLoading,
    blob,
    playbackRate,
    playPause,
    setPlaybackRate,
  } = useWaveformPlayer({
    url: isCompleted ? recordingUrl : null,
    useAuth: true,
    initialDuration: recording.recording_duration || 0,
    waveColor: '#94A3B8',
    progressColor: '#1d4ed8',
    barWidth: 2,
    barGap: 3,
    height: 24,
  });

  const handleDownload = () => {
    if (blob) {
      const ext = blob.type === 'audio/wav' ? 'wav' : 'mp3';
      saveAs(blob, `call_recording_${recording.session_id}.${ext}`);
    }
  };

  // Failed recording — nothing to show
  if (recording.recording_status === 'failed') {
    return null;
  }

  // In-progress — show pulsing red dot indicator
  if (recording.recording_status === 'in-progress') {
    return (
      <div>
        <div className="flex items-center gap-3 rounded-lg border border-stroke bg-background p-3">
          <span className="relative flex h-3 w-3 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-destructive" />
          </span>
          <span className="text-sm text-foreground-muted">
            {t('try_it.call_recording_in_progress')}
          </span>
        </div>
      </div>
    );
  }

  if (!recordingUrl) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center gap-3 rounded-lg border border-stroke bg-background p-3">
        <Button
          type="button"
          size="icon-sm"
          variant="outline-neutral"
          onClick={playPause}
          className="shrink-0"
          aria-label={isPlaying ? t('common.pause') : t('common.play')}
          disabled={!isReady}
        >
          {isPlaying ? <PauseIcon size={14} /> : <PlayIcon size={14} />}
        </Button>

        <div className="relative min-w-0 flex-1">
          <div ref={containerRef} className="h-6 cursor-pointer" />
          {!isReady && <Skeleton className="absolute inset-0 h-6 w-full" aria-busy={isLoading} />}
        </div>

        {isLoading && !duration ? (
          <Skeleton className="h-4 w-10 shrink-0" />
        ) : (
          <span className="shrink-0 text-xs text-foreground-muted tabular-nums">
            {formatSecondsToMmSs(duration - currentTime)}
          </span>
        )}

        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            size="icon-sm"
            variant="ghost-neutral"
            onClick={handleDownload}
            aria-label={t('common.download')}
            disabled={!blob}
          >
            <DownloadIcon size={14} />
          </Button>

          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="text-xs font-medium text-informational hover:text-informational/80 disabled:opacity-50"
                disabled={!isReady}
              >
                {playbackRate}x
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[1400] min-w-0">
              <span className="block px-2 py-1.5 text-xs font-medium text-foreground-muted">
                {t('try_it.playback_speed')}
              </span>
              {PLAYBACK_RATES.map((rate) => (
                <DropdownMenuItem
                  key={rate}
                  onSelect={() => setPlaybackRate(rate)}
                  className={playbackRate === rate ? 'font-semibold' : ''}
                >
                  {rate}x
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};
